import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/cc";
import Modal from "@/components/Modal";
import { Plus, MapPin, Banknote, Briefcase, X, Clock, Layers, FileEdit, Power, Archive, Eye } from "lucide-react";

const SERVICE_TYPES = ["Domiciliary Care", "Supported Living", "Day Services"];
const CONTRACT_TYPES = ["Permanent", "Fixed-term", "Bank/Relief"];

const STATUS_PILL = {
  draft: "pill-neutral",
  active: "pill-success",
  closed: "pill-warning",
  archived: "pill-danger",
};

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [modal, setModal] = useState(null); // {mode:'new'|'edit'|'view', job?}
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/jobs");
    setJobs(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = jobs.filter((j) => filter === "all" || j.status === filter);

  const changeStatus = async (job, status) => {
    try {
      await api.post(`/jobs/${job.id}/status`, { status });
      toast.success(`Job ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Recruitment</div>
          <h1 className="font-display text-4xl font-bold">Job Postings</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Open roles, drafts, closed and archived positions.</p>
        </div>
        <button className="btn-primary" onClick={() => setModal({ mode: "new" })} data-testid="new-job-button">
          <Plus className="w-4 h-4" /> New Job
        </button>
      </div>

      <div className="flex gap-2 mb-5">
        {[{ k: "all", l: "All" }, { k: "draft", l: "Drafts" }, { k: "active", l: "Active" }, { k: "closed", l: "Closed" }, { k: "archived", l: "Archived" }].map((t) => (
          <button key={t.k} className={`tab-btn ${filter === t.k ? "active" : ""}`} onClick={() => setFilter(t.k)} data-testid={`jobs-filter-${t.k}`}>
            {t.l} <span className="ml-1 text-[10px] text-[var(--cc-text-soft)]">{t.k === "all" ? jobs.length : jobs.filter((j) => j.status === t.k).length}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger">
        {filtered.map((j) => (
          <div key={j.id} className="cc-card cc-card-hover p-6" data-testid={`job-card-${j.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setModal({ mode: "view", job: j })}>
                <div className="font-display font-bold text-xl">{j.title}</div>
                <div className="text-xs text-[var(--cc-text-soft)] mt-1">{j.department} · {j.service_type || "—"}</div>
              </div>
              <span className={`pill ${STATUS_PILL[j.status]}`} data-testid={`job-status-${j.id}`}>{j.status}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-[var(--cc-text-soft)] mb-3">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location}</span>
              <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{j.salary}</span>
              <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{j.contract_type || j.employment_type}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{j.hours_per_week ? `${j.hours_per_week}h/wk` : "—"}</span>
            </div>
            <p className="text-sm text-[var(--cc-text-soft)] line-clamp-2">{j.description}</p>
            <div className="cc-divider mt-4 pt-3 flex items-center justify-between">
              <div className="text-xs text-[var(--cc-text-soft)]">Posted {fmtDate(j.created_at)} · <strong>{j.applicant_count}</strong> applicants</div>
              <div className="flex gap-1">
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setModal({ mode: "view", job: j })} data-testid={`view-job-${j.id}`}><Eye className="w-3.5 h-3.5" /></button>
                <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setModal({ mode: "edit", job: j })} data-testid={`edit-job-${j.id}`}><FileEdit className="w-3.5 h-3.5" /></button>
                {j.status === "draft" && (
                  <button className="btn-ghost px-2 py-1 text-xs" onClick={() => changeStatus(j, "active")} data-testid={`publish-job-${j.id}`} title="Publish">Publish</button>
                )}
                {j.status === "active" && (
                  <button className="btn-ghost px-2 py-1 text-xs" onClick={() => changeStatus(j, "closed")} data-testid={`close-job-${j.id}`} title="Close"><Power className="w-3.5 h-3.5" /></button>
                )}
                {j.status === "closed" && (
                  <>
                    <button className="btn-ghost px-2 py-1 text-xs" onClick={() => changeStatus(j, "active")} data-testid={`reopen-job-${j.id}`} title="Re-open">Re-open</button>
                    <button className="btn-ghost px-2 py-1 text-xs" onClick={() => changeStatus(j, "archived")} data-testid={`archive-job-${j.id}`} title="Archive"><Archive className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-2 text-center py-10 text-[var(--cc-text-soft)]">No jobs in this view.</div>}
      </div>

      {modal && <JobModal mode={modal.mode} job={modal.job} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function emptyForm() {
  return {
    title: "", location: "", department: "Care", employment_type: "Full-time",
    salary: "", description: "", requirements: "",
    service_type: "", contract_type: "", contract_end_date: "", hours_per_week: "",
  };
}

function JobModal({ mode, job, onClose, onSaved }) {
  const isView = mode === "view";
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => {
    if (!job) return emptyForm();
    return {
      ...emptyForm(),
      ...job,
      requirements: (job.requirements || []).join("\n"),
      contract_end_date: job.contract_end_date?.slice(0, 10) || "",
      hours_per_week: job.hours_per_week ?? "",
    };
  });
  const [busy, setBusy] = useState(false);

  const buildPayload = () => ({
    title: form.title,
    location: form.location,
    department: form.department,
    employment_type: form.employment_type,
    salary: form.salary,
    description: form.description,
    requirements: form.requirements.split("\n").map((r) => r.trim()).filter(Boolean),
    service_type: form.service_type || null,
    contract_type: form.contract_type || null,
    contract_end_date: form.contract_type === "Fixed-term" && form.contract_end_date ? new Date(form.contract_end_date).toISOString() : null,
    hours_per_week: form.hours_per_week === "" ? null : Number(form.hours_per_week),
  });

  const submit = async (status) => {
    if (!form.title) return toast.error("Title is required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/jobs/${job.id}`, buildPayload());
        toast.success("Job updated");
      } else {
        await api.post("/jobs", { ...buildPayload(), status });
        toast.success(status === "draft" ? "Draft saved" : "Job published");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (isView && job) {
    return (
      <Modal onClose={onClose} testId="job-view-modal" widthClass="max-w-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label-eyebrow mb-1">{job.department} · {job.service_type || "—"}</div>
            <h2 className="font-display text-3xl font-bold">{job.title}</h2>
          </div>
          <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm mb-5">
          <Detail label="Location" value={job.location} />
          <Detail label="Salary" value={job.salary} />
          <Detail label="Service Type" value={job.service_type} />
          <Detail label="Contract Type" value={job.contract_type} />
          <Detail label="Hours / week" value={job.hours_per_week} />
          <Detail label="Contract End" value={job.contract_end_date ? fmtDate(job.contract_end_date) : "—"} />
          <Detail label="Status" value={job.status} />
          <Detail label="Applicants" value={job.applicant_count} />
        </div>
        <div className="cc-divider pt-5">
          <div className="label-eyebrow mb-2">Description</div>
          <p className="text-sm text-[var(--cc-text-soft)] leading-relaxed mb-4">{job.description}</p>
        </div>
        {job.requirements?.length > 0 && (
          <div>
            <div className="label-eyebrow mb-2">Requirements</div>
            <ul className="text-sm text-[var(--cc-text-soft)] list-disc list-inside space-y-1">
              {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} testId={isEdit ? "job-edit-modal" : "new-job-modal"} widthClass="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold">{isEdit ? "Edit job" : "Create job posting"}</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Job title <span className="text-[var(--cc-accent)]">*</span></label>
            <input className="cc-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Service type <span className="text-[var(--cc-accent)]">*</span></label>
            <select className="cc-select" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="job-service-type" id="service-type-select">
              <option value="">Select…</option>
              {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Contract type <span className="text-[var(--cc-accent)]">*</span></label>
            <select className="cc-select" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} data-testid="job-contract-type" id="contract-type-select">
              <option value="">Select…</option>
              {CONTRACT_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {form.contract_type === "Fixed-term" && (
            <div className="col-span-2">
              <label className="label-eyebrow block mb-1">Contract end date <span className="text-[var(--cc-accent)]">*</span></label>
              <input type="date" className="cc-input max-w-xs" value={form.contract_end_date} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} data-testid="job-contract-end-date" id="contract-end-date-input" />
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-1">Hours per week <span className="text-[var(--cc-accent)]">*</span></label>
            <input type="number" min="0" max="168" className="cc-input" value={form.hours_per_week} onChange={(e) => setForm({ ...form, hours_per_week: e.target.value })} placeholder="e.g. 37.5" data-testid="job-hours-per-week" id="hours-per-week-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Salary</label>
            <input className="cc-input" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder="£24,000 - £27,500" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Location</label>
            <input className="cc-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Department</label>
            <input className="cc-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Description</label>
            <textarea className="cc-textarea min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Requirements (one per line)</label>
            <textarea className="cc-textarea min-h-[80px]" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="NVQ Level 3..." />
          </div>
        </div>
        <div className="text-xs text-[var(--cc-text-soft)] mt-3">
          <strong>Save as Draft</strong> keeps the job hidden from candidates — title is sufficient. <strong>Publish</strong> requires Service type, Contract type and Hours per week.
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          {isEdit ? (
            <button className="btn-primary" onClick={() => submit()} disabled={busy} data-testid="save-job-button">{busy ? "Saving…" : "Save changes"}</button>
          ) : (
            <>
              <button className="btn-secondary" onClick={() => submit("draft")} disabled={busy} data-testid="save-draft-button">Save as Draft</button>
              <button className="btn-primary" onClick={() => submit("active")} disabled={busy} data-testid="publish-job-button">Publish</button>
            </>
          )}
        </div>
    </Modal>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow mb-0.5">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}
